import { Box, Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import {
  DocumentCard,
  DocumentFilters,
  FileDropzone,
  IDocumentFile,
  IDocumentFilters,
  useDocumentFilters,
} from 'core/components';
import { Loader } from 'core/elements';
import {
  AccessPermissions,
  FileExtension,
  FileUploadType,
  MimeType,
  NotificationPosition,
} from 'core/enums';
import { useFilesActions } from 'core/hooks';
import { useGetJobFiles, useJobActions } from 'jobs/hooks';
import { IJob } from 'jobs/types';
import { Color, Direction, Level } from 'theme/enums';

interface IProps {
  job: IJob;
}

export const Documents = ({ job }: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();

  const { fileUpload } = useFilesActions({ position: NotificationPosition.TOP });
  const { attachJobFile, detachJobFile } = useJobActions();
  const { records, isLoading } = useGetJobFiles({ filters: { bomId: job.id } });

  const isEditAllowed =
    job.access?.editAllowed && hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_DETAILS_EDIT);

  const {
    filters,
    setFilters,
    getActiveFilterCount,
    clearFilters,
    filteredRecords,
    fileTypeOptions,
  } = useDocumentFilters<IDocumentFilters, IDocumentFile>({
    records,
  });

  const onUpload = async (files: File[]) => {
    const { data } = await fileUpload(files, {
      fileType: FileUploadType.BOM_ATTACHMENT,
    });

    const fileId = data[0].id;

    await attachJobFile({
      bomId: job.id,
      file: fileId,
    });
  };

  const onRemoveFile = async (fileId: number) => {
    await detachJobFile({
      ids: [fileId],
    });
  };

  const acceptFileTypes = {
    [MimeType.XLS]: [FileExtension.XLS],
    [MimeType.XLSX]: [FileExtension.XLSX],
    [MimeType.CSV]: [FileExtension.CSV],
    [MimeType.PDF]: [FileExtension.PDF],
    [MimeType.JPEG]: [FileExtension.JPEG],
    [MimeType.PNG]: [FileExtension.PNG],
  };

  return (
    <Stack gap={1}>
      {isEditAllowed && (
        <FileDropzone accept={acceptFileTypes} onUpload={onUpload} style={{ height: '113px' }} />
      )}

      {records.length > 0 && (
        <DocumentFilters
          filters={filters}
          setFilters={setFilters}
          activeFilterCount={getActiveFilterCount()}
          clearFilters={clearFilters}
          fileTypeOptions={fileTypeOptions}
        />
      )}

      <Box>
        {isLoading && <Loader />}
        {!isLoading && filteredRecords.length === 0 && (
          <Typography color={Color.NEUTRAL} level={Level.TITLE_SM} fontStyle="italic">
            {records.length ? t('messageNoMatchingFiles') : t('messageNoFilesAttached')}
          </Typography>
        )}
      </Box>

      <Stack
        direction={Direction.ROW}
        sx={{
          gap: 2,
          flexWrap: 'wrap',
          maxHeight: 'calc(100vh - 480px)',
          overflow: 'auto',
        }}
      >
        {filteredRecords.map((document) => (
          <DocumentCard
            key={document.id}
            file={document}
            onRemove={isEditAllowed ? onRemoveFile : undefined}
          />
        ))}
      </Stack>
    </Stack>
  );
};
